import { useEffect, useRef } from 'react';
import { startPolling, type OutageLog } from '../lib/polling';

interface UsePollingOptions {
  /** Poll only while true. */
  enabled: boolean;
  intervalMs: number;
  immediate?: boolean;
  label: string;
  log: OutageLog;
  /** The poll starts over (fresh cadence, backoff reset) when this changes. */
  restartKey?: unknown;
}

/**
 * startPolling() for a component: polls while `enabled`, always calling the
 * latest `tick`, and stops on unmount. Changing `tick` alone doesn't restart
 * the cadence; changing `enabled`, `intervalMs` or `restartKey` does.
 */
export function usePolling(tick: () => Promise<unknown>, options: UsePollingOptions): void {
  const { enabled, intervalMs, immediate = false, label, log, restartKey } = options;
  const tickRef = useRef(tick);
  tickRef.current = tick;
  useEffect(() => {
    if (!enabled) return;
    return startPolling(() => tickRef.current(), { intervalMs, immediate, label, log });
  }, [enabled, intervalMs, immediate, label, log, restartKey]);
}
